import { type RefObject, useEffect } from 'react';
import { Platform, type ScrollView } from 'react-native';

const EDGE_ZONE_PX = 120;
const MAX_GLIDE_SPEED_PX_PER_SECOND = 1100;
const MAX_FRAME_DELTA_MS = 100;
const MS_PER_SECOND = 1000;

interface WebScrollHost {
  getScrollableNode: () => unknown;
}

function isWebScrollHost(value: unknown): value is WebScrollHost {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as Partial<WebScrollHost>).getScrollableNode === 'function'
  );
}

function resolveScrollNode(ref: RefObject<ScrollView | null>): HTMLElement | null {
  const host: unknown = ref.current;
  if (!isWebScrollHost(host)) return null;
  const node = host.getScrollableNode();
  return node instanceof HTMLElement ? node : null;
}

function glideVelocityForPointer(pointerX: number, bounds: DOMRect): number {
  const depthIntoLeftZone = bounds.left + EDGE_ZONE_PX - pointerX;
  const depthIntoRightZone = pointerX - (bounds.right - EDGE_ZONE_PX);
  if (depthIntoLeftZone > 0) {
    return -MAX_GLIDE_SPEED_PX_PER_SECOND * Math.min(1, depthIntoLeftZone / EDGE_ZONE_PX);
  }
  if (depthIntoRightZone > 0) {
    return MAX_GLIDE_SPEED_PX_PER_SECOND * Math.min(1, depthIntoRightZone / EDGE_ZONE_PX);
  }
  return 0;
}

function attachEdgeGlide(node: HTMLElement): () => void {
  let velocity = 0;
  let frame: number | null = null;
  let lastFrameTime = 0;

  const glideFrame = (time: number): void => {
    const elapsedMs = Math.min(time - lastFrameTime, MAX_FRAME_DELTA_MS);
    node.scrollLeft += (velocity * elapsedMs) / MS_PER_SECOND;
    lastFrameTime = time;
    frame = velocity === 0 ? null : requestAnimationFrame(glideFrame);
  };

  const stopGlide = (): void => {
    velocity = 0;
    if (frame !== null) cancelAnimationFrame(frame);
    frame = null;
  };

  const glideAt = (nextVelocity: number): void => {
    if (nextVelocity === 0) {
      stopGlide();
      return;
    }
    velocity = nextVelocity;
    if (frame === null) {
      lastFrameTime = performance.now();
      frame = requestAnimationFrame(glideFrame);
    }
  };

  const handleDragOver = (event: DragEvent): void => {
    glideAt(glideVelocityForPointer(event.clientX, node.getBoundingClientRect()));
  };

  const handleDragLeave = (event: DragEvent): void => {
    const stillInside = event.relatedTarget instanceof Node && node.contains(event.relatedTarget);
    if (!stillInside) stopGlide();
  };

  node.addEventListener('dragover', handleDragOver);
  node.addEventListener('dragleave', handleDragLeave);
  document.addEventListener('drop', stopGlide, true);
  document.addEventListener('dragend', stopGlide, true);

  return () => {
    stopGlide();
    node.removeEventListener('dragover', handleDragOver);
    node.removeEventListener('dragleave', handleDragLeave);
    document.removeEventListener('drop', stopGlide, true);
    document.removeEventListener('dragend', stopGlide, true);
  };
}

export function useDragEdgeGlide(scrollRef: RefObject<ScrollView | null>, enabled: boolean): void {
  useEffect(() => {
    if (!enabled || Platform.OS !== 'web') return;
    const node = resolveScrollNode(scrollRef);
    if (!node) return;
    return attachEdgeGlide(node);
  }, [scrollRef, enabled]);
}
